import React, { useState, useEffect } from "react";
import { Button, Input, Form } from "reactstrap";
import FontAwesomeButton from '../../../misc/FontAwesomeButton';
import { useTranslation } from "react-i18next";
import { AddClassroomMembersType, AddedClassroomMember, CoreClassroomAdminClassroom } from "../../../../models/CoreClassroomAdminClassroom";
import { RequestState } from "../../../../models/RequestState";
import { RootState } from "../../../../store/store";
import { ClassroomAddMembers, ClearClassroomAddMembers } from "../../../../store/ClassroomAdmin/ClassroomToolsAction";
import { useDispatch, useSelector } from "react-redux";
import StatusCode from "../../../../util/StatusCode";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from "../../../misc/Loading";


interface ClassroomViewToolAddMembersProps {
    type?: AddClassroomMembersType
}

const ClassroomViewToolAddMembers = (props: ClassroomViewToolAddMembersProps) => {
    const { t } = useTranslation(['common', "classroomAdmin"]);
    const dispatch = useDispatch();

    const [members, setMembers] = useState<string[]>([]);
    const [isAddingMember, setIsAddingMember] = useState<boolean>(false);
    const [newMemberValue, setNewMemberValue] = useState<string>("");
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState<string>('');

    const classroom = useSelector<RootState, RequestState<CoreClassroomAdminClassroom>>(x => x.classroomAdminClassroom.classroom);
    const addedMembers = useSelector<RootState, RequestState<AddedClassroomMember[]>>(x => x.classroomTools.addMembersClassroom);

    useEffect(() => {
        return () => {
            ClearClassroomAddMembers()(dispatch);
        };
    }, []);

    const startEditMember = (index: number) => {
        setEditingIndex(index);
        setEditingValue(members[index]);
    };

    const saveEditedMember = () => {
        if (editingIndex === null) {
            return;
        }

        setMembers(prev => prev.map((member, index) => index === editingIndex ? editingValue : member));
        setEditingIndex(null);
        setEditingValue('');
        setIsAddingMember(true);
    };

    const removeMember = (indexToRemove: number) => {
        setMembers(prev => prev.filter((_, index) => index !== indexToRemove));

        if (editingIndex === null) {
            return;
        }

        if (editingIndex === indexToRemove) {
            setEditingIndex(null);
            setEditingValue('');
            return;
        }

        if (editingIndex > indexToRemove) {
            setEditingIndex(editingIndex - 1);
        }
    };

    const addMember = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!newMemberValue.trim()) {
            return;
        }

        setMembers(prev => [...prev, newMemberValue.trim()]);
        setNewMemberValue('');
        setIsAddingMember(true);
    };

    const addMembers = () => {
        if (!members.length) {
            return null;
        }
        if (classroom.code === StatusCode.COMPLETE && classroom.data && props.type !== undefined) {
            ClassroomAddMembers(classroom.data.classroomId, { type: props.type, members: members })(dispatch);
        }
    }

    const showAddInput = members.length === 0 || isAddingMember || addedMembers.code === StatusCode.COMPLETE;

    const renderState = () => {
        if (addedMembers.code === StatusCode.PENDING) {
            return <Loading />;
        }
        if (addedMembers.code === StatusCode.ERROR) {
            const errorData = addedMembers.error?.response?.data as { title?: string; detail?: string } | undefined;
            const title = errorData?.title;
            var errorMessage = title ? t(`tools.errors.general.${title}`, { ns: 'classroomAdmin' }) : t(`error.somethingWentWrong`, { ns: 'common' });
            return (<>
                <FontAwesomeIcon icon="triangle-exclamation" color="red" style={{ marginRight: '5px' }} />
                {errorMessage}
            </>);
        }
    }

    const isNewMemberValueValid = newMemberValue.trim().length >= 5;
    const isEditingValueValid = editingValue.trim().length >= 5;
    const hasOnlyValidMembers = members.every(member => member.trim().length > 5);

    return (<>
        {members.map((member, index) => {
            const addedMember = addedMembers.code === StatusCode.COMPLETE
                ? addedMembers.data?.find(x => x.email === member)
                : undefined;

            return (<div key={`${member}-${index}`} >
                {editingIndex === index ?
                    <Form onSubmit={() => saveEditedMember()} className="flex space-between mb-3 align-items-center">
                        <Input
                            type="text"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            placeholder={t('tools.ADD_MEMBERS.placeHolder', { ns: 'classroomAdmin' })}
                        />
                        <div className="flex flex-row align-items-center">
                            <Button type="submit" disabled={!isEditingValueValid} color="primary" className="ms-2">{t('action.save', { ns: 'common' })}</Button>
                            <Button color="secondary" className="ms-2" onClick={() => (setEditingIndex(null), setIsAddingMember(true))}>{t('action.cancel', { ns: 'common' })}</Button>
                        </div>
                    </Form>
                    : (addedMembers.code === StatusCode.NONE) ?
                        <div className="flex space-between align-items-center border-bottom ps-2 pe-2 pt-2 pb-2">
                            <span>{member}</span>
                            <div className="flex flex-row align-items-center">
                                <span className="me-3"><FontAwesomeButton icon="pen" onClick={() => (startEditMember(index), setIsAddingMember(false))} title={t('tools.ADD_MEMBERS.edit', { ns: 'classroomAdmin' })} /></span>
                                <FontAwesomeButton icon="trash" onClick={() => (removeMember(index), setIsAddingMember(true))} title={t('action.remove', { ns: 'common' })} />
                            </div>
                        </div>
                        : <div className="flex align-items-center border-bottom ps-2 pe-2 pt-2 pb-2">
                            <span className="w-50">{member}</span>
                            {addedMembers.code === StatusCode.COMPLETE ?
                                addedMember?.success ?
                                    <FontAwesomeIcon icon="check" color="green" />
                                    : <><FontAwesomeIcon icon="triangle-exclamation" color="red" className="me-2" />
                                        <span>{addedMember?.error
                                            ? t(`tools.errors.members.${addedMember.error}`, { ns: 'classroomAdmin', defaultValue: addedMember.error })
                                            : null}</span></>
                                : null}
                        </div>
                }
            </div>);
        })}

        {addedMembers.code === StatusCode.NONE ?
            showAddInput ? (<>
                <Form onSubmit={(e) => addMember(e)} className="flex space-between align-items-center mt-3">
                    <Input
                        type="text"
                        value={newMemberValue}
                        onChange={(e) => setNewMemberValue(e.target.value)}
                        placeholder={t('tools.ADD_MEMBERS.placeHolder', { ns: 'classroomAdmin' })}
                    />
                    <Button color="primary" disabled={!isNewMemberValueValid} className="ms-3 text-nowrap" type="submit">{t('action.add', { ns: 'common' })}</Button>
                </Form>
            </>)
                : <Button color="secondary" onClick={() => setIsAddingMember(true)}>+</Button>
            : null}

        <div className="flex align-items-center mt-3">
            <Button onClick={() => addMembers()} disabled={members.length < 1 || addedMembers.code !== StatusCode.NONE || !hasOnlyValidMembers || editingIndex !== null} className="me-3">
                {props.type === AddClassroomMembersType.Student ? t('tools.ADD_MEMBERS.studentTitle', { ns: 'classroomAdmin' })
                    : props.type === AddClassroomMembersType.Teacher ? t('tools.ADD_MEMBERS.teacherTitle', { ns: 'classroomAdmin' })
                        : null}
            </Button>
            {renderState()}
        </div>
    </>);
}

export default ClassroomViewToolAddMembers;
